import { useState } from 'react';

import { Center, Flex, Spinner, Text } from '@chakra-ui/react';

import { PaginationState } from '@tanstack/react-table';

import EmptyState from 'app/components/UI/EmptyState';
import { Table } from 'app/components/UI/Table/Table';

import useFetchDeductionAdjustments from 'app/hooks/useFetchDeductionAdjustments';

import getDeductionAdjustmentColumns from 'app/utils/table/deductionAdjustments';

import { DeductionAdjustmentsListProps } from './types';

const PAGE_SIZE = 10;

/** Lists the deduction adjustments made for a person, or on one of their reports, newest first. */
function DeductionAdjustmentsList({
  individualIds = undefined,
  individualReportIds = undefined,
}: DeductionAdjustmentsListProps) {
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });

  const { data, isLoading, isError } = useFetchDeductionAdjustments({
    individualIds,
    individualReportIds,
    page: pageIndex,
    size: pageSize,
  });

  const renderContent = () => {
    if (isLoading) {
      return (
        <Center w="full" py="20px" data-testid="deduction-adjustments-loading">
          <Spinner color="brand.purple" size="lg" />
        </Center>
      );
    }
    if (isError) {
      return (
        <EmptyState text="Sorry, we failed to fetch the deduction adjustments. Please try again." />
      );
    }
    if (!data?.list.length) {
      return <EmptyState text="No deduction adjustments have been made" />;
    }
    return (
      <Table
        columns={getDeductionAdjustmentColumns()}
        data={data.list}
        total={data.total}
        pageNumber={pageIndex}
        pageSize={pageSize}
        onPaginationChange={setPagination}
        getRowId={(adjustment) => adjustment.id}
      />
    );
  };

  return (
    <Flex flexDir="column" gap="10px" w="full" data-testid="deduction-adjustments-list">
      <Text as="h5" textStyle="h5SemiBold" color="brand.darkGray">
        Deduction adjustments
      </Text>
      {renderContent()}
    </Flex>
  );
}

export default DeductionAdjustmentsList;
